'use client';

import { FC, ReactNode, useEffect, useRef } from 'react';
import { useState } from 'react';
import { CarouselContext, TCarouselContext } from './CarouselContext';
import { getResponsiveValue, ResponsiveValue } from '../../../utils';
import { useScreenName } from '../../../hooks/use-screen-name';

type Props = {
  enabled?: ResponsiveValue<boolean>;
  children?: ReactNode;
  initialIndex?: number;
};

/**
 * The UniversalCarousel component provides a flexible and easily customizable UI element that's perfect for showcasing a collection of items that users can scroll through horizontally. The component is comprised of three main elements:
 *
 * 1. **`UniversalCarousel`**: The main component that creates the context for the carousel.
 * 2. **`CarouselStage`**: A wrapper for the items within the carousel. It handles the layout and the scroll behavior of the carousel items.
 * 3. **`CarouselInterface`**: An optional component that provides control mechanisms (like next and previous buttons) and indicators (like current position).
 *
 * Here's a basic example of how to use the `UniversalCarousel` component:
 *
 * ```tsx *
 * <UniversalCarousel>
 *   <CarouselStage>
 *     <img src="..." alt="" />
 *     <img src="..." alt="" />
 *     <img src="..." alt="" />
 *     ...
 *   </CarouselStage>
 *   <CarouselInterface>
 *     {({ prev, next }) => (
 *       <>
 *         <button onClick={prev}>Previous</button>
 *         <button onClick={next}>Next</button>
 *       </>
 *     )}
 *   </CarouselInterface>
 * </UniversalCarousel>
 * ```
 *
 * ## Components
 *
 * ### UniversalCarousel
 *
 * The `UniversalCarousel` component sets up the context for the carousel and its items. It doesn't render any DOM elements by itself but provides the scaffolding necessary for the `CarouselStage` and `CarouselInterface`.
 *
 * #### Props
 *
 * - `children`: The elements to be displayed within the carousel. These are usually a combination of `CarouselStage`, `CarouselInterface` and other custom elements you need.
 *
 * ### CarouselStage
 *
 * `CarouselStage` is where the carousel items are housed. It is a `ul` element that renders each child as an `li`, ensuring proper semantics and accessibility.
 *
 * > 👉 Review the **Carousel Stage** documentation to learn more and view usage examples.
 *
 * ### CarouselInterface
 *
 * `CarouselInterface` is an optional component that exposes controls like "next" and "previous" buttons, and indicators like the current position within the carousel.
 *
 * > 👉 Review the **Carousel Interface** documentation to learn more and view usage examples.
 *
 * ## Accessibility
 *
 * While the UniversalCarousel component has been designed with accessibility in mind, it's important to ensure that any custom controls or additional UI elements you add are also accessible. This includes proper use of ARIA attributes, ensuring keyboard navigation is possible, when feasable, and providing alternative text for images.
 */
export const UniversalCarousel: FC<Props> = ({
  enabled: enabledArg,
  children,
  initialIndex = 0,
}) => {
  const screenName = useScreenName();
  const enabled = getResponsiveValue(enabledArg, true, screenName);

  const [contextValue, setContextValue] = useState<TCarouselContext>({
    enabled,
    visibleItems: 0,
    visibleIndexes: [initialIndex],
    lastIndex: 0,
    stageScrollLeft: 0,
    stageScrollWidth: 0,
    stageWidth: 0,
    scrollToIndex: () => {},
    set: () => {},
  });

  const isInitialized = useRef(false);

  // Scroll to the initial index when the carousel is first rendered.
  useEffect(() => {
    if (
      !isInitialized.current &&
      contextValue.scrollToIndex &&
      initialIndex > 0
    ) {
      const timer = setTimeout(() => {
        contextValue.scrollToIndex(initialIndex, 'start', 'instant');
        isInitialized.current = true;
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [initialIndex, contextValue.scrollToIndex, contextValue]);

  useEffect(() => {
    if (contextValue.enabled === enabled) {
      return;
    }

    setContextValue(prev => ({
      ...prev,
      enabled,
    }));
  }, [contextValue.enabled, enabled, setContextValue]);

  return (
    <CarouselContext.Provider value={{ ...contextValue, set: setContextValue }}>
      {children}
    </CarouselContext.Provider>
  );
};
